import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { publicApi, formatApiError } from "@/lib/api";

const FIELDS = {
  demo: [
    { name: "name", label: "Full name", type: "text", required: true, placeholder: "Jane Doe" },
    { name: "email", label: "Work email", type: "email", required: true, placeholder: "jane@company.com" },
    { name: "company", label: "Company", type: "text", required: true, placeholder: "Company Inc." },
    { name: "role", label: "Role", type: "text", required: false, placeholder: "Head of Network Operations" },
    { name: "message", label: "What you would like to see", type: "textarea", required: false, placeholder: "Topology, priorities, constraints…" },
  ],
  pilot: [
    { name: "name", label: "Full name", type: "text", required: true, placeholder: "Jane Doe" },
    { name: "email", label: "Work email", type: "email", required: true, placeholder: "jane@company.com" },
    { name: "company", label: "Company", type: "text", required: true, placeholder: "Company Inc." },
    { name: "role", label: "Role", type: "text", required: true, placeholder: "Infrastructure Director" },
    { name: "environment_size", label: "Environment size", type: "text", required: false, placeholder: "e.g. 1k nodes, 3 regions" },
    { name: "timeline", label: "Target pilot window", type: "text", required: false, placeholder: "e.g. Q1 next year" },
    { name: "message", label: "What success looks like", type: "textarea", required: false, placeholder: "Priorities, outcomes, integration needs…" },
  ],
  // Launch notification for Halo / Fortress while sales are closed.
  // Email only; no company or marketing profile is collected.
  launch: [
    { name: "email", label: "Email", type: "email", required: true, placeholder: "you@example.com" },
    { name: "name", label: "First name", type: "text", required: false, placeholder: "Alex" },
  ],
  investor: [
    { name: "name", label: "Full name", type: "text", required: true, placeholder: "Jane Doe" },
    { name: "email", label: "Work email", type: "email", required: true, placeholder: "jane@firm.com" },
    { name: "firm", label: "Firm", type: "text", required: true, placeholder: "Firm Capital" },
    { name: "role", label: "Role", type: "text", required: false, placeholder: "Partner" },
    { name: "stage_focus", label: "Stage focus", type: "text", required: false, placeholder: "e.g. Series B+" },
    { name: "message", label: "What you'd like to review", type: "textarea", required: false, placeholder: "Briefing focus, diligence questions…" },
  ],
};

// Launch-notify consent notice. Recorded with each request so the one launch
// email can be traced to explicit permission (docs/PRODUCT_OFFERS_CONSENT.md).
export const LAUNCH_NOTIFY_NOTICE_VERSION = "launch-notify-2026-10-05";
export const LAUNCH_NOTIFY_PRODUCTS = Object.freeze({
  halo: "Halo SuperVPN",
  "halo-fortress": "Fortress",
  "halo-fortress-pro": "Fortress Pro",
});

export default function LeadDialog({ open, onOpenChange, kind, title, description, successHref, successLabel, product }) {
  const fields = FIELDS[kind] || FIELDS.demo;
  const launch = kind === "launch";
  const launchProduct = LAUNCH_NOTIFY_PRODUCTS[product] ? product : "halo-fortress";
  const launchProductName = LAUNCH_NOTIFY_PRODUCTS[launchProduct];
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setValues({});
    setSuccess(false);
    setError("");
    setSubmitting(false);
  };

  const handleChange = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      // Strip empty optional fields so backend validation doesn't choke on ""
      const payload = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v !== "" && v != null)
      );
      if (launch && payload.notify_consent !== true) {
        // No email is ever recorded for launch news without explicit consent.
        throw new Error("Tick the box so we can email you when sales open.");
      }
      const launchFields = launch
        ? { product: launchProduct, notify_consent: true, consent_notice_version: LAUNCH_NOTIFY_NOTICE_VERSION }
        : {};
      const { data } = await publicApi.post(`/leads/${kind}`, {
        ...payload,
        ...launchFields,
        source_url: window.location.href,
      });
      if (data?.demo_url) setValues((current) => ({ ...current, demo_url: data.demo_url }));
      setSuccess(true);
      toast.success(launch ? "You're on the list." : "Thank you. We will reach out shortly.");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] max-w-xl overflow-y-auto rounded-none border-ink-100 p-0"
        data-testid={`lead-dialog-${kind}`}
      >
        <div className="p-6 md:p-8">
          <DialogHeader>
            <div className="lf-eyebrow">
              {launch ? "Launch notification" : kind === "investor" ? "Investor briefing" : kind === "pilot" ? "Pilot intake" : "Enterprise demo"}
            </div>
            <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-2">
              {title}
            </DialogTitle>
            <DialogDescription className="text-ink-500 text-sm mt-1">
              {description}
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="mt-8 border border-ink-100 p-6 flex gap-4" data-testid={`lead-success-${kind}`}>
              <CheckCircle2 className="h-6 w-6 text-electric flex-shrink-0" strokeWidth={1.75} />
              <div>
                <p className="font-display text-lg font-bold">{launch ? "You're on the list." : "Request received."}</p>
                <p className="text-sm text-ink-500 mt-1">
                  {launch
                    ? `We'll email you once when ${launchProductName} goes on sale. Nothing else.`
                    : "Your details have been securely recorded. We will follow up within one business day."}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  {kind === "demo" && (values.demo_url || successHref) ? <a className="lf-btn-primary" href={values.demo_url || successHref} target="_blank" rel="noreferrer" data-testid="lead-success-demo-link">{successLabel || "Open LightningFlow demo"}<ArrowRight className="h-4 w-4" /></a> : null}
                  <button type="button" className="lf-btn-secondary" onClick={() => onOpenChange(false)} data-testid={`lead-success-close-${kind}`}>Close</button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-5" data-testid={`lead-form-${kind}`}>
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="lf-label" htmlFor={`${kind}-${f.name}`}>
                    {f.label}
                    {f.required ? <span className="text-electric ml-1">*</span> : null}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      id={`${kind}-${f.name}`}
                      className="lf-textarea"
                      placeholder={f.placeholder}
                      required={f.required}
                      value={values[f.name] || ""}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                      data-testid={`lead-input-${kind}-${f.name}`}
                    />
                  ) : (
                    <input
                      id={`${kind}-${f.name}`}
                      type={f.type}
                      className="lf-input"
                      placeholder={f.placeholder}
                      required={f.required}
                      value={values[f.name] || ""}
                      onChange={(e) => handleChange(f.name, e.target.value)}
                      data-testid={`lead-input-${kind}-${f.name}`}
                    />
                  )}
                </div>
              ))}

              <input className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" value={values.website || ""} onChange={(e) => handleChange("website", e.target.value)} />

              {launch ? (
                <label className="flex gap-3 text-sm leading-relaxed text-ink-600">
                  <input className="mt-1 h-4 w-4 accent-electric" type="checkbox" required checked={values.notify_consent || false} onChange={(e) => handleChange("notify_consent", e.target.checked)} data-testid="lead-launch-consent" />
                  <span>Email me once when {launchProductName} goes on sale. This is not a sign-up for product offers.</span>
                </label>
              ) : null}

              <label className="flex gap-3 text-sm leading-relaxed text-ink-600">
                <input className="mt-1 h-4 w-4 accent-electric" type="checkbox" required checked={values.privacy_consent || false} onChange={(e) => handleChange("privacy_consent", e.target.checked)} />
                <span>I agree to the website <Link className="underline" to="/privacy">privacy handling</Link> for this {launch ? "request" : "enquiry"}.</span>
              </label>

              {error ? (
                <p className="text-sm text-red-700" data-testid={`lead-error-${kind}`}>
                  {error}
                </p>
              ) : null}

              <div className="flex items-center justify-between pt-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
                  {launch ? "One email when sales open." : "No marketing spam. Reply from a human."}
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="lf-btn-primary"
                  data-testid={`lead-submit-${kind}`}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Submitting…" : launch ? "Notify me" : "Submit request"}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
